import { Component, OnDestroy, OnInit, inject, output, signal, viewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  faCheck,
  faClock,
  faPaperclip,
  faPenToSquare,
  faProjector,
} from '@fortawesome/pro-solid-svg-icons';
import { EditorComponent, EditorCssSettings } from '@progress/kendo-angular-editor';
import { PopupSettings } from '@progress/kendo-angular-popup';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { defaultPalette } from '../../../core/constants-repository/color-palette.constants';
import { Priorities } from '../../../core/constants-repository/priority-values.constants';
import { UserOperation } from '../../../core/enums/user-operation';
import { TodoEntryModel } from '../../../core/models/todo-planner/todo-entry.model';
import { ShivePopupService } from '../../../core/services/controls/shive-popup.service';
import { TodoPlannerHttpService } from '../../../core/state/todo-planner/todo-planner-http.service';
import { PRIORITY_LABELS } from '../../../core/text-db/priority-values.text';
import { DropdownOption } from '../../../shared/data-types/dropdown-option';
import { FormControlNameRecord } from '../../../shared/data-types/form-types';
import { DateTimeHelper } from '../../calendar/util/date-time-helper';
import { TodoFileUploadPopupComponent } from '../popups/todo-file-upload-popup/todo-file-upload-popup.component';
import { TodoPlannerService } from '../services/todo-planner.service';
import { TodoEntryCrudService } from '../todo-entry/services/todo-entry-crud.service';
import { TodoEntryService } from '../todo-entry/services/todo-entry.service';

@Component({
  selector: 'app-todo-planner-sidebar-form',
  templateUrl: './todo-planner-sidebar-form.component.html',
  styleUrl: './todo-planner-sidebar-form.component.scss',
  standalone: false,
})
export class TodoPlannerSidebarFormComponent implements OnInit, OnDestroy {
  // Signals
  readonly todoTitleForm = signal<FormGroup | null>(null);
  readonly todoEntryForm = signal<FormGroup | null>(null);
  readonly finishedLoading = signal(false);
  readonly todoEntryModel = signal<TodoEntryModel | null>(null);
  readonly popupSettings = signal<PopupSettings>({
    anchorAlign: { horizontal: 'right', vertical: 'bottom' },
    popupAlign: { horizontal: 'right', vertical: 'top' },
    margin: { horizontal: 0, vertical: 8 },
  });

  // Viewchild
  readonly editor = viewChild<EditorComponent>('editor');

  // Inputs / Outputs
  readonly closeSidebar$ = output<void>();

  public readonly formControlNames: FormControlNameRecord = {
    Title: 'name',
    TodoColor: 'color',
    Priority: 'priority',
    StartsAt: 'startsAt',
    EndsAt: 'endsAt',
    Completed: 'completed',
    Editor: 'editor',
  };
  public readonly popupContent = TodoFileUploadPopupComponent;

  public readonly priorityOptions: DropdownOption[] = (
    Object.values(Priorities) as Priorities[]
  ).map((priority) => ({
    value: priority,
    text: PRIORITY_LABELS[priority],
  }));
  public readonly defaultPalette = defaultPalette;
  public readonly faPenToSquare = faPenToSquare;
  public readonly faCheck = faCheck;
  public readonly UserOperation = UserOperation;
  public readonly faPaperclip = faPaperclip;
  public readonly faClock = faClock;
  public readonly faProjector = faProjector;
  public readonly iframeStyles: EditorCssSettings = {
    path: 'assets/config/editor-config.css',
  };

  // DI
  private readonly todoPlannerService = inject(TodoPlannerService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly todoEntryCrudService = inject(TodoEntryCrudService);
  private readonly todoPlannerHttpService = inject(TodoPlannerHttpService);
  private readonly shivePopupService = inject(ShivePopupService);

  // Misc
  private readonly subs = new SubSink();

  ngOnInit(): void {
    this.subs.sink = this.todoPlannerService.sendTodoEntryModel$
      .pipe(filter((model) => !!model))
      .subscribe((todoEntryModel) => {
        this.todoEntryModel.set(todoEntryModel);
        this.finishedLoading.set(true);
        this.initForm(todoEntryModel);
      });
  }

  ngOnDestroy(): void {
    this.todoPlannerService.sendTodoEntryModel$.next(null);
    this.subs.unsubscribe();
  }

  public update(): void {
    const controls = this.todoEntryForm().controls;

    const currentModel = this.todoEntryCrudService.selectTodoEntry(this.todoEntryModel().id);
    const clonedEntry = TodoEntryService.cloneTodoModel(currentModel);
    clonedEntry.name = this.todoTitleForm().controls[this.formControlNames.Title].value;
    clonedEntry.start = controls[this.formControlNames.StartsAt].value;
    clonedEntry.end = controls[this.formControlNames.EndsAt].value;
    clonedEntry.color = controls[this.formControlNames.TodoColor].value;
    clonedEntry.priority = controls[this.formControlNames.Priority].value;
    clonedEntry.description = controls[this.formControlNames.Editor].value;
    clonedEntry.fixInterval = !DateTimeHelper.isSameDay(clonedEntry.start, clonedEntry.end);
    clonedEntry.completed = this.todoEntryModel().completed;

    this.todoPlannerHttpService.updateTodoPlannerEntry(clonedEntry);
    this.closeSidebar$.emit();
  }

  public delete(): void {
    const clonedTodoEntry = TodoEntryService.cloneTodoModel(this.todoEntryModel());
    this.todoPlannerHttpService.deleteTodoPlannerEntry(clonedTodoEntry);
  }

  public close(): void {
    this.closeSidebar$.emit();
  }

  public propChanged([value, formCtrlName]: [string | Date | boolean | number, string]) {
    this.todoEntryModel.update((old) => {
      const clonedEntry = TodoEntryService.cloneTodoModel(old, false);
      clonedEntry[formCtrlName] = value;
      return clonedEntry;
    });
  }

  public startDateChanged(startDate: any): void {
    this.propChanged([startDate, this.formControlNames.StartsAt]);
    this.propChanged([startDate, this.formControlNames.EndsAt]);

    this.todoEntryForm().controls[this.formControlNames.EndsAt].setValue(startDate);
  }

  public onAssignProjectPopupClose(updatedTodoEntry: TodoEntryModel): void {
    this.propChanged([updatedTodoEntry.projectId, 'projectId']);
    this.propChanged([updatedTodoEntry.color, this.formControlNames.Color]);

    this.todoEntryForm().controls[this.formControlNames.TodoColor].setValue(updatedTodoEntry.color);

    this.shivePopupService.close();
  }

  private initForm(todoEntryModel: TodoEntryModel): void {
    const titleForm = this.formBuilder.group({
      [this.formControlNames.Title]: [todoEntryModel.name],
    });
    this.todoTitleForm.set(titleForm);

    const form = this.formBuilder.group({
      [this.formControlNames.TodoColor]: [todoEntryModel.color],
      [this.formControlNames.Priority]: [todoEntryModel.priority],
      [this.formControlNames.StartsAt]: [todoEntryModel.start],
      [this.formControlNames.EndsAt]: [todoEntryModel.end],
      [this.formControlNames.Editor]: [todoEntryModel.description],
    });
    this.todoEntryForm.set(form);
  }
}
